import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Loader2, ImagePlus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthImage } from "./AuthImage";
import { FilePickButton } from "./FilePickButton";
import { swatchImageUrl, refreshFabricLibrary } from "./useFabricLibrary";
import { normalizeFabricPart, makeFabricKey } from "./fabricUtils";
import { postSwatch, SWATCH_MAX_SIZE } from "./swatchUpload";

const CODE_RE = /^(?=.*\d)[A-Z0-9]{4,5}$/;

function NewSwatchForm({ code, collections, testId, onCancel, onCreated }) {
  const [collection, setCollection] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  const save = async () => {
    setSaving(true);
    try {
      const created = await postSwatch(collection, code, file);
      await refreshFabricLibrary();
      toast.success(`Toile ${code} ajoutée à la bibliothèque`);
      onCreated(created?.swatch?.key || makeFabricKey(collection, code));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 p-3 rounded-lg border border-orange-200 bg-orange-50/60 space-y-2" data-testid={`${testId}-new-form`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-800">Nouvelle toile <span className="tracking-wide">{code}</span></p>
        <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-orange-100" data-testid={`${testId}-new-cancel`}><X className="w-4 h-4 text-zinc-500" /></button>
      </div>
      <div>
        <label className="text-xs font-medium text-zinc-600">De quelle collection s'agit-il ?</label>
        <Input value={collection} onChange={(e) => setCollection(e.target.value)} list={`${testId}-collections`} placeholder="S&P SELECTION 2025" className="h-8 bg-white mt-1" autoFocus data-testid={`${testId}-new-collection`} />
        <datalist id={`${testId}-collections`}>{collections.map((c) => <option key={c} value={c} />)}</datalist>
      </div>
      <div className="flex items-center gap-2">
        {preview && <img src={preview} alt="" className="w-14 h-9 object-cover rounded border border-zinc-300" />}
        <FilePickButton accept="image/*" onFile={setFile} testId={`${testId}-new-file`} className="h-8 text-xs gap-1.5 bg-white">
          <ImagePlus className="w-3.5 h-3.5" />{file ? "Changer la photo" : "Choisir la photo"}
        </FilePickButton>
        <span className="text-[10px] text-zinc-500">Réduite à {SWATCH_MAX_SIZE} px</span>
      </div>
      <Button size="sm" onClick={save} disabled={saving || !collection.trim() || !file} className="w-full h-8 gap-1.5 bg-orange-500 hover:bg-orange-600" data-testid={`${testId}-new-submit`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Ajouter à la bibliothèque et à la commande
      </Button>
    </div>
  );
}

// Type a fabric code: suggestions come from the swatch library; unknown codes can be created with their photo.
export function FabricCodePicker({ library, existingKeys, onPick, autoFocus = false, testId = "fabric-code-picker" }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(null);
  const q = normalizeFabricPart(query);

  const suggestions = useMemo(() => {
    if (!q) return [];
    return library.list
      .filter((s) => !existingKeys.has(s.key) && (s.code.includes(q) || s.collection.includes(q)))
      .sort((a, b) => Number(b.code.startsWith(q)) - Number(a.code.startsWith(q)) || a.code.localeCompare(b.code))
      .slice(0, 8);
  }, [library.list, q, existingKeys]);
  const collections = useMemo(() => [...new Set(library.list.map((s) => s.collection).filter(Boolean))].sort(), [library.list]);
  const canCreate = CODE_RE.test(q);
  const codeExists = library.byCode.has(q);

  const pick = (key) => { onPick(key); setQuery(""); setCreating(null); };
  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (suggestions[0]) pick(suggestions[0].key);
    else if (canCreate) setCreating(q);
  };

  return (
    <div data-testid={testId}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={query} onChange={(e) => { setQuery(e.target.value); setCreating(null); }} onKeyDown={onKeyDown} autoFocus={autoFocus}
          placeholder="Ajouter une toile par code (ex. 00197)…" className="pl-9 h-9" data-testid={`${testId}-input`} />
      </div>
      {q && !creating && (
        <div className="mt-1 rounded-lg border border-zinc-200 bg-white shadow-sm max-h-60 overflow-y-auto" data-testid={`${testId}-results`}>
          {suggestions.map((s, i) => (
            <button key={s.swatch_id} type="button" onClick={() => pick(s.key)} className="w-full flex items-center gap-3 px-3 py-1.5 hover:bg-orange-50 text-left transition-colors" data-testid={`${testId}-suggestion-${i}`}>
              <AuthImage src={swatchImageUrl(s)} alt={s.code} className="w-12 h-8 object-cover rounded border border-zinc-200 flex-shrink-0" />
              <span className="min-w-0"><span className="block font-bold text-sm text-zinc-900 tracking-wide">{s.code}</span><span className="block text-xs text-zinc-500 truncate">{s.collection || "Sans collection"}</span></span>
            </button>
          ))}
          {canCreate && (
            <button type="button" onClick={() => setCreating(q)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-orange-700 hover:bg-orange-50 border-t border-zinc-100 transition-colors" data-testid={`${testId}-create`}>
              <Plus className="w-4 h-4" />{codeExists ? `Autre collection : nouvelle photo pour « ${q} »` : `Nouvelle toile « ${q} » : ajouter sa photo`}
            </button>
          )}
          {!suggestions.length && !canCreate && <p className="px-3 py-2 text-xs text-zinc-500" data-testid={`${testId}-empty`}>Aucune toile trouvée (un code fait 4 ou 5 caractères)</p>}
        </div>
      )}
      {creating && <NewSwatchForm code={creating} collections={collections} testId={testId} onCancel={() => setCreating(null)} onCreated={pick} />}
    </div>
  );
}
